import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Store
import { Dispatch, RootState } from '../../../../store/store';

// Components
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../../../components/InputControl/InputControl';
import { ButtonType } from '../../../../types/button-type';
import { FilePreview } from './FirePreview';
import ROUTE from '../../../../constants/route-constants';
import { generatePath } from 'react-router-dom';

type FormValues = {
  file: File | null;
  startAt: string;
  dueAt: string;
};

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const CalibrationCertificateFormView: React.FC<ComponentProps> = ({
  calibrationCertificate,
  createCalibrationCertificate,
  getCalibrationCertificate,
  resetCalibrationCertificate,
  updateCalibrationCertificate,
}) => {
  const navigate = useNavigate();
  const { id: controllerId, certificateId } = useParams();

  useEffect(() => {
    // Clear previous data when component mounts
    resetCalibrationCertificate();

    // get calibration certificate when Edit
    if (certificateId) getCalibrationCertificate(certificateId);

    return () => {
      resetCalibrationCertificate();
    };
  }, [certificateId, getCalibrationCertificate, resetCalibrationCertificate]);

  const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0]; // Extract only "yyyy-MM-dd"
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      file: null,
      startAt: calibrationCertificate?.startAt || '',
      dueAt: calibrationCertificate?.dueAt || ''
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      startAt: Yup.date().required('Start date is required'),
      dueAt: Yup.date()
        .required('Due date is required')
        .min(Yup.ref('startAt'), 'Due date must be after start date'),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        if (values.file) {
          formData.append('file', values.file);
        }
        formData.append('startAt', values.startAt);
        formData.append('dueAt', values.dueAt);
        formData.append('controllerId', controllerId);

        if (calibrationCertificate) {
          const body = {
            startAt: values.startAt,
            dueAt: values.dueAt,
            controllerId,
          };
          await updateCalibrationCertificate({ id: certificateId, body });
        } else {
          await createCalibrationCertificate({ formData });
        }
        navigate(generatePath(ROUTE.CALIBRATION_CERTIFICATES, { id: controllerId }));
      } catch (error) {
        console.error('Failed to handle calibration certificate:', error);
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      formik.setFieldValue('file', event.target.files[0]);
    }
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  const onCancel = () => {
    navigate(generatePath(ROUTE.CALIBRATION_CERTIFICATES, { id: controllerId }));
  };

  const getFileError = () => {
    if (!formik.touched.file || !formik.errors.file) return '';
    if (typeof formik.errors.file === 'string') return formik.errors.file;
    return 'Invalid file';
  };

  return (
    <div className="p-calibration-certificate-form pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">
            {calibrationCertificate ? 'Update Calibration Certificate' : 'Create Calibration Certificate'}
          </h3>
        </div>
        <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
          <div className="w-full">
            <InputControl
              label="Start Date"
              type="date"
              name="startAt"
              value={formatDate(formik.values.startAt)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.startAt}
              errorText={formik.errors.startAt}
            />
          </div>
          <div className="w-full">
            <InputControl
              label="Due Date"
              type="date"
              name="dueAt"
              value={formatDate(formik.values.dueAt)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showError={formik.touched.dueAt}
              errorText={formik.errors.dueAt}
            />
          </div>
          <div className="w-full">
            <InputControl
              label="Calibration Certificate File"
              type="file"
              name="file"
              onChange={handleFileChange}
              onBlur={formik.handleBlur}
              showError={true}
              errorText={getFileError()}
              disabled={!!calibrationCertificate}
            />

            {calibrationCertificate && <FilePreview file={calibrationCertificate.file} />}
          </div>
          <div className="flex justify-end gap-4">
            <InteractiveButton
              type={ButtonType.PrimaryInverse}
              text="Cancel"
              onClick={onCancel}
            />
            <InteractiveButton
              type={ButtonType.Primary}
              text={calibrationCertificate ? 'Update' : 'Create'}
              onClick={handleSubmit}
              disabled={!formik.isValid || formik.isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  calibrationCertificate: state.calibration_certificates.calibrationCertificate,
});

const mapDispatch = (dispatch: Dispatch) => ({
  createCalibrationCertificate: dispatch.calibration_certificates.createCalibrationCertificate,
  getCalibrationCertificate: dispatch.calibration_certificates.getCalibrationCertificate,
  resetCalibrationCertificate: dispatch.calibration_certificates.resetOne,
  updateCalibrationCertificate: dispatch.calibration_certificates.updateCalibrationCertificate,
});

export default connect(mapState, mapDispatch)(CalibrationCertificateFormView); 